/* eslint-disable indent */
import React, { useState, useEffect, useRef } from "react";
import { DatePicker, Button, Typography, message, Skeleton } from "antd";


const { RangePicker } = DatePicker;
import { ArrowLeftOutlined } from "@ant-design/icons";
import { useNavigate, useLocation } from "react-router-dom";
import moment from "moment";
import { formatIndianNumber } from "../../../../utils";
import { DirectorDashboardService3 } from "../../../../services/api/directorDashboardService3";
import axios from "axios";
import "./style.scss";

import "../style.scss";

const { Title } = Typography;

const ZoneWiseView3 = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [selectedDate, setSelectedDate] = useState(moment());
    const [selectedDateRange, setSelectedDateRange] = useState([moment().startOf("month"), moment()]);
    const [loading, setLoading] = useState(false);
    const [zoneWiseData, setZoneWiseData] = useState({});
    const [reportCategory, setReportCategory] = useState("Business Data"); // Business Data, Shipment Counts, Delivery Data
    const [dataType, setDataType] = useState("today"); // "today" or "range"
    const hasLoadedRef = useRef(false);
    const isInitializingRef = useRef(true);

    const zones = ["EAST", "NORTH", "SOUTH1", "SOUTH2", "WEST1", "WEST2"];

    useEffect(() => {
        // Get report category, type, and data type from location state if available
        const category = location.state?.reportCategory || "Business Data";
        const typeMode = location.state?.type || "today";
        const businessType = location.state?.businessType || null;

        // Set flag to indicate we're initializing
        isInitializingRef.current = true;

        setReportCategory(category);
        setDataType(typeMode);

        // If businessType is provided, navigate to ZoneList route instead
        if (businessType) {
            navigate("/zone-list2", {
                state: {
                    type: typeMode,
                    reportType: location.state?.reportType || "Piece Rate",
                    reportCategory: category,
                    date: location.state?.date || moment().format("YYYY-MM-DD"),
                    businessType: businessType,
                },
            });
            return;
        }

        // Set date from location.state (this is the date from API response)
        if (typeMode === "today") {
            // For "today" type, set single date
            if (location.state?.date) {
                // Parse the date from location.state (format: "YYYY-MM-DD")
                const dateValue = moment(location.state.date, "YYYY-MM-DD", true);

                // Only set if valid, otherwise use default
                if (dateValue.isValid()) {
                    setSelectedDate(dateValue);
                } else {
                    // Fallback: try parsing without strict mode
                    const fallbackDate = moment(location.state.date);
                    setSelectedDate(fallbackDate.isValid() ? fallbackDate : moment());
                }
            } else {
                // If no date in state, use default (today)
                setSelectedDate(moment());
            }
        } else {
            // For "range" type, set date range
            if (location.state?.date && location.state?.endDate) {
                // Parse dates from location.state (format: "YYYY-MM-DD")
                const startDate = moment(location.state.date, "YYYY-MM-DD", true);
                const endDate = moment(location.state.endDate, "YYYY-MM-DD", true);

                if (startDate.isValid() && endDate.isValid()) {
                    setSelectedDateRange([startDate, endDate]);
                } else {
                    // Fallback: use month start to today
                    setSelectedDateRange([moment().startOf("month"), moment()]);
                }
            } else if (location.state?.date) {
                // Only start date available, use it as both start and end
                const dateValue = moment(location.state.date, "YYYY-MM-DD", true);
                if (dateValue.isValid()) {
                    setSelectedDateRange([dateValue, dateValue]);
                } else {
                    setSelectedDateRange([moment().startOf("month"), moment()]);
                }
            } else {
                // If no date in state, use default (month start to today)
                setSelectedDateRange([moment().startOf("month"), moment()]);
            }
        }

        // Mark initialization as complete after state updates
        // Use requestAnimationFrame to ensure state updates are processed
        requestAnimationFrame(() => {
            setTimeout(() => {
                isInitializingRef.current = false;
            }, 0);
        });
    }, [location.state, navigate]);

    // Transform API response to component format
    const transformApiData = (apiData, dateKey, category) => {
        const dateData = apiData?.data?.[dateKey] || {};
        const transformed = {};

        if (category === "Business Data") {
            // Transform business data
            transformed.cash = {};
            transformed.account = {};
            transformed.dp = {};
            transformed.pieceRate = {};

            zones.forEach((zone) => {
                const zoneData = dateData[zone]?.business || {};
                transformed.cash[zone] = zoneData.Cash || 0;
                transformed.account[zone] = zoneData.Account || 0;
                transformed.dp[zone] = zoneData.DP || 0;
                transformed.pieceRate[zone] = zoneData.Piece || 0;
            });
        } else if (category === "Shipment Counts") {
            // Transform shipment count data
            transformed.cashCount = {};
            transformed.accountCount = {};
            transformed.dpCounts = {};
            transformed.pieceRateCount = {};

            zones.forEach((zone) => {
                const zoneData = dateData[zone]?.shipment_count || {};
                transformed.cashCount[zone] = zoneData.Cash || 0;
                transformed.accountCount[zone] = zoneData.Account || 0;
                transformed.dpCounts[zone] = zoneData.DP || 0;
                transformed.pieceRateCount[zone] = zoneData.Piece || 0;
            });
        } else if (category === "Delivery Data") {
            // Transform delivery data
            transformed.totalDrs = {};
            transformed.deliveredShipments = {};
            transformed.rtoDelivery = {};
            transformed.pendingShipments = {};
            transformed.returnShipments = {}; // Maps to "Return Initiated" from API
            transformed.undeliveredShipments = {}; // Not in API, set to 0

            zones.forEach((zone) => {
                const zoneData = dateData[zone]?.drs || {};
                transformed.totalDrs[zone] = zoneData["Total DRS"] || 0;
                transformed.deliveredShipments[zone] = zoneData.Delivered || 0;
                transformed.rtoDelivery[zone] = zoneData["RTO Delivered"] || 0;
                transformed.pendingShipments[zone] = zoneData.Pending || 0;
                transformed.returnShipments[zone] = zoneData["Return Initiated"] || 0;
                transformed.undeliveredShipments[zone] = zoneData["Undelivered"] || 0; // Not available in API
            });
        }

        return transformed;
    };

    useEffect(() => {
        // Load data when reportCategory/dataType/selectedDate or selectedDateRange changes
        // Check if date is set based on dataType
        if (dataType === "today" && !selectedDate) return;
        if (dataType === "range" && (!selectedDateRange || selectedDateRange.length !== 2)) return;

        // Create a unique key for this combination to prevent duplicate calls
        const loadKey = dataType === "today"
            ? `${reportCategory}-${dataType}-${selectedDate.format("YYYY-MM-DD")}`
            : `${reportCategory}-${dataType}-${selectedDateRange[0].format("YYYY-MM-DD")}-${selectedDateRange[1].format("YYYY-MM-DD")}`;

        const controller = new AbortController();

        // Define the fetch function
        const fetchZoneWiseData = async () => {
            setLoading(true);

            try {
                // Use date from selectedDate or selectedDateRange based on dataType
                let fromDate, toDate;

                if (dataType === "today") {
                    // For "today" type, use the selectedDate (from DatePicker or location.state)
                    fromDate = selectedDate.format("YYYY-MM-DD");
                    toDate = selectedDate.format("YYYY-MM-DD");
                } else {
                    // For range, use selectedDateRange
                    if (selectedDateRange && selectedDateRange.length === 2 && selectedDateRange[0] && selectedDateRange[1]) {
                        fromDate = selectedDateRange[0].format("YYYY-MM-DD");
                        toDate = selectedDateRange[1].format("YYYY-MM-DD");
                    } else {
                        // Fallback: use location.state dates or defaults
                        fromDate = location.state?.date || moment().startOf("month").format("YYYY-MM-DD");
                        toDate = location.state?.endDate || location.state?.date || moment().format("YYYY-MM-DD");
                    }
                }

                // Call API
                const response = await DirectorDashboardService3.getZoneWiseData(fromDate, toDate);

                if (response?.data) {
                    // For date range, we might need to aggregate data across multiple dates
                    let dateKey = toDate;

                    // If range and multiple dates exist, aggregate them
                    if (dataType === "range" && response.data.data) {
                        const dateKeys = Object.keys(response.data.data);
                        if (dateKeys.length > 0) {
                            // Aggregate data across all dates
                            const aggregated = {};
                            dateKeys.forEach((key) => {
                                const dateData = response.data.data[key];
                                zones.forEach((zone) => {
                                    if (!aggregated[zone]) {
                                        aggregated[zone] = {
                                            business: {},
                                            shipment_count: {},
                                            drs: {},
                                        };
                                    }

                                    // Aggregate business data
                                    Object.keys(dateData[zone]?.business || {}).forEach((metric) => {
                                        aggregated[zone].business[metric] =
                                            (aggregated[zone].business[metric] || 0) +
                                            (dateData[zone]?.business?.[metric] || 0);
                                    });

                                    // Aggregate shipment_count data
                                    Object.keys(dateData[zone]?.shipment_count || {}).forEach((metric) => {
                                        aggregated[zone].shipment_count[metric] =
                                            (aggregated[zone].shipment_count[metric] || 0) +
                                            (dateData[zone]?.shipment_count?.[metric] || 0);
                                    });

                                    // Aggregate drs data
                                    Object.keys(dateData[zone]?.drs || {}).forEach((metric) => {
                                        aggregated[zone].drs[metric] =
                                            (aggregated[zone].drs[metric] || 0) +
                                            (dateData[zone]?.drs?.[metric] || 0);
                                    });
                                });
                            });

                            // Create a synthetic date key for aggregated data
                            const syntheticData = {
                                data: {
                                    [toDate]: aggregated,
                                },
                            };
                            const transformed = transformApiData(syntheticData, toDate, reportCategory);
                            setZoneWiseData(transformed);
                            setLoading(false);
                            return;
                        }
                    }

                    // Transform single date data
                    const transformed = transformApiData(response.data, dateKey, reportCategory);
                    setZoneWiseData(transformed);
                } else {
                    setZoneWiseData({});
                    message.warning("No data available for the selected date range");
                }
            } catch (error) {
                if (axios.isCancel(error)) return;
                console.error("Error loading zone wise data:", error);
                message.error(error?.message || "Failed to load zone wise data");
                setZoneWiseData({});
            } finally {
                setLoading(false);
            }
        };

        // Skip if still initializing (to prevent calls during initial state setup)
        if (isInitializingRef.current) {
            // Wait a bit for initialization to complete, then call API
            const timer = setTimeout(() => {
                if (!isInitializingRef.current && (!hasLoadedRef.current || hasLoadedRef.current !== loadKey)) {
                    hasLoadedRef.current = loadKey;
                    fetchZoneWiseData();
                }
            }, 150);
            return () => clearTimeout(timer);
        }

        // Only call if this is a new combination
        if (!hasLoadedRef.current || hasLoadedRef.current !== loadKey) {
            hasLoadedRef.current = loadKey;
            fetchZoneWiseData();
        }

        return () => {
            controller.abort();
            // clear timer if exists (though timer variable is local to closure above)
        };
    }, [reportCategory, dataType, selectedDate, selectedDateRange]);


    const handleDateSubmit = () => {
        // Reset the ref to allow manual reload with new date/range
        hasLoadedRef.current = null;

        // Create a unique key for the current selection
        const loadKey = dataType === "today"
            ? `${reportCategory}-${dataType}-${selectedDate.format("YYYY-MM-DD")}`
            : `${reportCategory}-${dataType}-${selectedDateRange[0].format("YYYY-MM-DD")}-${selectedDateRange[1].format("YYYY-MM-DD")}`;

        // Call the API directly
        const fetchZoneWiseData = async () => {
            setLoading(true);

            try {
                // Use date from selectedDate or selectedDateRange based on dataType
                let fromDate, toDate;

                if (dataType === "today") {
                    fromDate = selectedDate.format("YYYY-MM-DD");
                    toDate = selectedDate.format("YYYY-MM-DD");
                } else {
                    if (selectedDateRange && selectedDateRange.length === 2 && selectedDateRange[0] && selectedDateRange[1]) {
                        fromDate = selectedDateRange[0].format("YYYY-MM-DD");
                        toDate = selectedDateRange[1].format("YYYY-MM-DD");
                    } else {
                        fromDate = location.state?.date || moment().startOf("month").format("YYYY-MM-DD");
                        toDate = location.state?.endDate || location.state?.date || moment().format("YYYY-MM-DD");
                    }
                }

                // Call API
                const response = await DirectorDashboardService3.getZoneWiseData(fromDate, toDate);

                if (response?.data) {
                    let dateKey = toDate;

                    // If range and multiple dates exist, aggregate them
                    if (dataType === "range" && response.data.data) {
                        const dateKeys = Object.keys(response.data.data);
                        if (dateKeys.length > 0) {
                            const aggregated = {};
                            dateKeys.forEach((key) => {
                                const dateData = response.data.data[key];
                                zones.forEach((zone) => {
                                    if (!aggregated[zone]) {
                                        aggregated[zone] = {
                                            business: {},
                                            shipment_count: {},
                                            drs: {},
                                        };
                                    }

                                    Object.keys(dateData[zone]?.business || {}).forEach((metric) => {
                                        aggregated[zone].business[metric] =
                                            (aggregated[zone].business[metric] || 0) +
                                            (dateData[zone]?.business?.[metric] || 0);
                                    });

                                    Object.keys(dateData[zone]?.shipment_count || {}).forEach((metric) => {
                                        aggregated[zone].shipment_count[metric] =
                                            (aggregated[zone].shipment_count[metric] || 0) +
                                            (dateData[zone]?.shipment_count?.[metric] || 0);
                                    });

                                    Object.keys(dateData[zone]?.drs || {}).forEach((metric) => {
                                        aggregated[zone].drs[metric] =
                                            (aggregated[zone].drs[metric] || 0) +
                                            (dateData[zone]?.drs?.[metric] || 0);
                                    });
                                });
                            });

                            const syntheticData = {
                                data: {
                                    [toDate]: aggregated,
                                },
                            };
                            const transformed = transformApiData(syntheticData, toDate, reportCategory);
                            setZoneWiseData(transformed);
                            setLoading(false);
                            return;
                        }
                    }

                    const transformed = transformApiData(response.data, dateKey, reportCategory);
                    setZoneWiseData(transformed);
                } else {
                    setZoneWiseData({});
                    message.warning("No data available for the selected date range");
                }
            } catch (error) {
                console.error("Error loading zone wise data:", error);
                message.error(error?.message || "Failed to load zone wise data");
                setZoneWiseData({});
            } finally {
                setLoading(false);
            }
        };

        hasLoadedRef.current = loadKey;
        fetchZoneWiseData();
    };

    // Use date from location.state for display (this is the date from API response)
    const getDisplayDate = () => {
        if (dataType === "today") {
            // For "today" type, use the date from location.state
            if (location.state?.date) {
                const dateMoment = moment(location.state.date, "YYYY-MM-DD", true);
                return dateMoment.isValid() ? dateMoment.format("DD/MM/YYYY") : selectedDate.format("DD/MM/YYYY");
            }
            return selectedDate.format("DD/MM/YYYY");
        } else {
            // For range, use dates from location.state
            if (location.state?.date && location.state?.endDate) {
                const fromDateMoment = moment(location.state.date, "YYYY-MM-DD", true);
                const toDateMoment = moment(location.state.endDate, "YYYY-MM-DD", true);
                if (fromDateMoment.isValid() && toDateMoment.isValid()) {
                    return `${fromDateMoment.format("DD/MM/YYYY")} - ${toDateMoment.format("DD/MM/YYYY")}`;
                }
            }
            // Fallback to selectedDate
            const rangeStartDate = selectedDate.startOf("month").format("DD/MM/YYYY");
            const rangeEndDate = selectedDate.format("DD/MM/YYYY");
            return `${rangeStartDate} - ${rangeEndDate}`;
        }
    };
    const displayDate = getDisplayDate();

    const handleGoBack = () => {
        navigate("/director-dashboardNew2");
    };

    // Get row types based on report category
    const getRowTypes = () => {
        if (reportCategory === "Business Data") {
            return [
                { key: "cash", label: "Cash", dataKey: "cash" },
                { key: "account", label: "Account", dataKey: "account" },
                { key: "dp", label: "DP", dataKey: "dp" },
                { key: "pieceRate", label: "Piece Rate", dataKey: "pieceRate" },
            ];
        } else if (reportCategory === "Shipment Counts") {
            return [
                { key: "cashCount", label: "Cash Count", dataKey: "cashCount" },
                { key: "accountCount", label: "Account Count", dataKey: "accountCount" },
                { key: "dpCounts", label: "DP Counts", dataKey: "dpCounts" },
                { key: "pieceRateCount", label: "Piece Rate Count", dataKey: "pieceRateCount" },
            ];
        } else if (reportCategory === "Delivery Data") {
            return [
                { key: "totalDrs", label: "Total DRS", dataKey: "totalDrs" },
                { key: "deliveredShipments", label: "Delivered Shipments", dataKey: "deliveredShipments" },
                { key: "returnShipments", label: "Return Shipments", dataKey: "returnShipments" },
                { key: "undeliveredShipments", label: "Undelivered Shipments", dataKey: "undeliveredShipments" },
                { key: "pendingShipments", label: "Pending Shipments", dataKey: "pendingShipments" },
                { key: "rtoDelivery", label: "RTO Delivery", dataKey: "rtoDelivery" },
            ];
        }
        return [];
    };

    // Calculate totals for each row and column
    const calculateRowTotal = (dataKey) => {
        const data = zoneWiseData[dataKey] || {};
        return zones.reduce((sum, zone) => sum + (data[zone] || 0), 0);
    };

    const calculateColumnTotal = (zone) => {
        const rowTypes = getRowTypes().filter((type) => type.dataKey !== "totalDrs");
        return rowTypes.reduce((sum, type) => {
            const data = zoneWiseData[type.dataKey] || {};
            return sum + (data[zone] || 0);
        }, 0);
    };

    const calculateGrandTotal = () => {
        return zones.reduce((sum, zone) => sum + calculateColumnTotal(zone), 0);
    };



    // Calculate zone specific total
    const getZoneTotalValue = (zone) => {
        const rowTypes = getRowTypes();
        return rowTypes.reduce((sum, type) => {
            const data = zoneWiseData[type.dataKey] || {};
            return sum + (data[zone] || 0);
        }, 0);
    };

    // Calculate specific metric total across all zones
    const getMetricTotalValue = (metricKey) => {
        return calculateRowTotal(metricKey);
    };

    return (
        <div className="director-dashboard zone-wise-page">
            {/* Header Section */}
            <div className="zone-wise-header">
                <div className="header-left">
                    <Button
                        type="text"
                        icon={<ArrowLeftOutlined />}
                        onClick={handleGoBack}
                        className="back-button"
                    />
                    <Title level={4} className="page-title">
                        Zone Wise {reportCategory} - {displayDate}
                    </Title>
                </div>

                <div className="header-right">
                    {/* Date Filter moved here */}
                    <div className="zone-wise-date-filter">
                        {dataType === "today" ? (
                            <DatePicker
                                format="DD/MM/YYYY"
                                value={selectedDate}
                                onChange={(date) => setSelectedDate(date || moment())}
                                className="zone-wise-date-picker"
                                allowClear={false}
                            />
                        ) : (
                            <RangePicker
                                format="DD/MM/YYYY"
                                value={selectedDateRange}
                                onChange={(dates) => {
                                    if (dates && dates.length === 2) {
                                        setSelectedDateRange([dates[0], dates[1]]);
                                    } else {
                                        setSelectedDateRange([moment().startOf("month"), moment()]);
                                    }
                                }}
                                className="zone-wise-date-picker"
                                allowClear={false}
                            />
                        )}
                        <Button type="primary" onClick={handleDateSubmit} className="zone-wise-submit-btn">
                            Submit
                        </Button>
                    </div>
                </div>
            </div>

            {/* Zone Cards Grid */}
            <div className="zone-cards-grid">
                {loading ? (
                    // Render 6 Skeleton cards to match the zones length
                    Array.from({ length: 6 }).map((_, index) => (
                        <div className="zone-data-card" key={`skeleton-${index}`}>
                            <div className="card-left-section">
                                <Skeleton.Input active size="small" style={{ width: 60, marginBottom: 8 }} />
                                <Skeleton.Input active size="large" style={{ width: 100 }} />
                            </div>

                            <div className="card-divider"></div>

                            <div className="card-right-section">
                                <div className="metrics-grid">
                                    {Array.from({ length: 4 }).map((_, i) => (
                                        <div className="metric-item" key={i}>
                                            <Skeleton.Input active size="small" style={{ width: 40, marginBottom: 4 }} />
                                            <Skeleton.Input active size="default" style={{ width: 60 }} />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    ))
                ) : (
                    zones.map((zone) => {

                        const zoneTotal = getZoneTotalValue(zone);
                        const rowTypes = getRowTypes();

                        return (
                            <div className="zone-data-card" key={zone}>
                                <div className="card-left-section">
                                    <div className="zone-name">{zone}</div>
                                    <div className="zone-total-value">
                                        {reportCategory === "Business Data" ? "₹" : ""}
                                        {formatIndianNumber(zoneTotal)}
                                        {reportCategory === "Business Data" && ".00"}
                                    </div>
                                </div>

                                <div className="card-divider"></div>

                                <div className="card-right-section">
                                    <div className="metrics-grid">
                                        {rowTypes.map((type) => {
                                            const val = zoneWiseData[type.dataKey]?.[zone] || 0;
                                            return (
                                                <div className="metric-item" key={type.key}>
                                                    <div className="metric-label">{type.label}</div>
                                                    <div className="metric-value">
                                                        {reportCategory === "Business Data" ? "₹ " : ""}
                                                        {formatIndianNumber(val)}
                                                        {reportCategory === "Business Data" && ".00"}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>

            {/* Footer Totals */}
            {!loading && (
                <div className="zone-wise-footer">
                    <div className="footer-metrics">
                        {getRowTypes().map((type) => {
                            const totalVal = getMetricTotalValue(type.dataKey);
                            return (
                                <div className="footer-metric-item" key={type.key}>
                                    <div className="f-metric-label">{type.label}</div>
                                    <div className="f-metric-value">
                                        {reportCategory === "Business Data" ? "₹ " : ""}
                                        {formatIndianNumber(totalVal)}
                                        {reportCategory === "Business Data" && ".00"}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    <div className="footer-grand-total">
                        <span className="grand-total-label">
                            {reportCategory === "Business Data" ? "Total Amount :" : "Total Count :"}
                        </span>
                        <span className="grand-total-value">

                            {reportCategory === "Business Data" ? "₹" : ""}
                            {formatIndianNumber(calculateGrandTotal())}
                            {reportCategory === "Business Data" && ".00"}
                        </span>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ZoneWiseView3;

